// @boty/i18n - Localized navigation helpers
import { createNavigation } from 'next-intl/navigation';
import { locales, localePrefix, type Locale } from './config';

/**
 * Localized navigation utilities from next-intl
 * These provide type-safe navigation that automatically handles locale prefixes
 */
export const { Link, redirect, usePathname, useRouter } = createNavigation({
  locales,
  localePrefix,
});

/**
 * Extract the locale from a pathname
 * @param pathname - The URL pathname (e.g., '/en/about' or '/about')
 * @returns The locale if found at the start of the path, or null
 * @example
 * getLocaleFromPathname('/en/about') // 'en'
 * getLocaleFromPathname('/he/dashboard') // 'he'
 * getLocaleFromPathname('/about') // null
 */
export function getLocaleFromPathname(pathname: string): Locale | null {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment && locales.includes(firstSegment as Locale)) {
    return firstSegment as Locale;
  }

  return null;
}

/**
 * Add or replace the locale prefix in a pathname
 * @param pathname - The URL pathname to localize
 * @param locale - The target locale
 * @returns The pathname with the locale prefix
 * @example
 * localizePathname('/about', 'en') // '/en/about'
 * localizePathname('/en/about', 'he') // '/he/about'
 */
export function localizePathname(pathname: string, locale: Locale): string {
  // Remove existing locale prefix if present
  const segments = pathname.split('/').filter(Boolean);
  const existingLocale = getLocaleFromPathname(pathname);

  if (existingLocale) {
    segments.shift(); // Remove existing locale
  }

  return `/${locale}/${segments.join('/')}`;
}

/**
 * Remove the locale prefix from a pathname
 * @param pathname - The URL pathname to delocalize
 * @returns The pathname without the locale prefix
 * @example
 * delocalizePathname('/en/about') // '/about'
 * delocalizePathname('/about') // '/about'
 */
export function delocalizePathname(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  const existingLocale = getLocaleFromPathname(pathname);

  if (existingLocale) {
    segments.shift();
  }

  return `/${segments.join('/')}`;
}
